import { timeOptions } from "@/views/job-add/components/JobDetailsForm";
import pick from "lodash/pick";

import { LocationPreferenceEnum } from "@/types/candidate";
import { JobFormType, JobType, JobUpdateType } from "@/types/job";

import {
  joiningAvailabilityOptions,
  LAKH_CURRENCY_MULTIPLIER,
} from "@/constants";
import { jobStatusOption } from "@/constants/jobs";

export const transformJobDataToFormData = (jobDetail: JobType) => {
  const formData: JobFormType = { ...jobDetail };

  if (!jobDetail.max_exp) {
    formData.max_exp = "";
  }
  if (!jobDetail.min_exp) {
    formData.min_exp = "";
  }
  if (!jobDetail.negotiable_max_salary) {
    formData.negotiable_max_salary = "";
  }
  if (!jobDetail.negotiable_min_exp) {
    formData.negotiable_min_exp = "";
  }

  formData.min_salary = jobDetail.min_salary / LAKH_CURRENCY_MULTIPLIER;
  formData.max_salary = jobDetail.max_salary / LAKH_CURRENCY_MULTIPLIER;

  formData.status__form_value = jobStatusOption.find(
    (item) => item.value === jobDetail.status,
  );

  const computedLocationPreference: any = {};
  jobDetail.work_preference_new?.forEach(
    (item) => (computedLocationPreference[item] = true),
  );
  ["Remote", "Hybrid", "Onsite"].forEach((item) => {
    if (!Object.keys(computedLocationPreference).includes(item)) {
      computedLocationPreference[item] = false;
    }
  });
  formData.work_preference_new__form_value = computedLocationPreference;

  if (jobDetail.is_codemonk_handpick === true) {
    formData.is_codemonk_handpick = "true";
  } else {
    formData.is_codemonk_handpick = "false";
    formData.handpick_company = undefined;
  }

  if (jobDetail.is_public === true) {
    formData.is_public = "true";
  } else if (jobDetail.is_public === false) {
    formData.is_public = "false";
  }

  if (jobDetail.target_date) {
    formData.target_date = [jobDetail.target_date];
  }

  if (jobDetail.interview_rounds) {
    formData.interview_rounds = jobDetail.interview_rounds.map((item) => ({
      id: item.interview_round.id,
      name: item.interview_round.name,
      category: {
        value: item.interview_round.category.id,
        label: item.interview_round.category.name,
      },
    }));
  } else {
    formData.interview_rounds = [];
  }

  if (jobDetail.priority_skills) {
    formData.priority_skills__form_value = [
      ...(jobDetail?.priority_skills?.skills_in_priority ?? [[]]),
      [],
      jobDetail?.priority_skills?.non_deal_breaker_skills ?? [],
    ];
  } else {
    formData.priority_skills__form_value = [];
  }

  if (jobDetail.joining_availability_days) {
    formData.joining_availability_days__form_value =
      joiningAvailabilityOptions.find(
        (item) =>
          item.value === jobDetail.joining_availability_days?.toString(),
      );
  }

  if (jobDetail.negotiable_min_joining_availability) {
    formData.negotiable_min_joining_availability__form_value =
      joiningAvailabilityOptions.find(
        (item) =>
          item.value ===
          jobDetail.negotiable_min_joining_availability?.toString(),
      );
  }

  if (jobDetail.working_hours_start) {
    formData.working_hours_start__form_value = timeOptions.find(
      (item) => item.value === jobDetail.working_hours_start,
    );
  }

  if (jobDetail.working_hours_end) {
    formData.working_hours_end__form_value = timeOptions.find(
      (item) => item.value === jobDetail.working_hours_end,
    );
  }

  return formData;
};

export const transformFormDataToJobData = (
  formData: JobFormType,
  pickFields: (keyof JobUpdateType)[],
) => {
  const jobDetails: JobUpdateType = {
    ...formData,
    id: formData.id || undefined,
  };

  if (jobDetails.min_salary)
    jobDetails.min_salary = jobDetails.min_salary * LAKH_CURRENCY_MULTIPLIER;

  if (jobDetails.max_salary)
    jobDetails.max_salary = jobDetails.max_salary * LAKH_CURRENCY_MULTIPLIER;

  // key will change thats why not added in type
  // if (formData.contacts?.length) {
  //   formData.contacts = formData.contacts;
  // }

  //if zelevate
  if (formData.is_codemonk_handpick === "false") {
    jobDetails.is_codemonk_handpick = false;
  } else {
    //if codemonk
    jobDetails.is_codemonk_handpick = true;
  }

  if (formData.is_public === "false") {
    jobDetails.is_public = false;
    jobDetails.sensitive_fields = [];
  } else {
    jobDetails.is_public = true;
    jobDetails.sensitive_fields = formData.sensitive_fields || [];
  }

  if (formData.target_date) {
    jobDetails.target_date = formData.target_date[0];
  }
  if (formData.work_preference_new__form_value) {
    if (
      !formData.work_preference_new__form_value.Hybrid &&
      !formData.work_preference_new__form_value.Onsite
    ) {
      jobDetails.location_new = [];
    }

    jobDetails.work_preference_new = (
      Object.keys(
        formData.work_preference_new__form_value,
      ) as LocationPreferenceEnum[]
    )
      .filter(
        (item: LocationPreferenceEnum) =>
          formData.work_preference_new__form_value &&
          formData.work_preference_new__form_value[item] === true,
      )
      .map((item) => item);
  }

  jobDetails.status = formData.status__form_value?.value;

  if (formData.interview_rounds) {
    jobDetails.interview_rounds = formData.interview_rounds.map((item) => ({
      ...(item.id && item.id % 1 === 0 ? { id: item.id } : {}),
      name: item.name,
      category: item.category.value,
    }));
  }

  if (formData.joining_availability_days__form_value) {
    jobDetails.joining_availability_days =
      formData.joining_availability_days__form_value.value;
  }
  if (formData.negotiable_min_joining_availability__form_value) {
    jobDetails.negotiable_min_joining_availability =
      formData.negotiable_min_joining_availability__form_value.value;
  }

  if (formData.working_hours_start__form_value) {
    jobDetails.working_hours_start =
      formData.working_hours_start__form_value.value;
  }

  if (formData.working_hours_end__form_value) {
    jobDetails.working_hours_end = formData.working_hours_end__form_value.value;
  }

  if (formData.priority_skills__form_value) {
    let finalSkillsData = [...formData.priority_skills__form_value];

    const [nonDealBreakerSkills] = finalSkillsData.splice(-1);

    finalSkillsData = finalSkillsData.filter(
      (skillGroup) => skillGroup.length !== 0,
    );
    jobDetails.priority_skills = {
      skills_in_priority: finalSkillsData,
      non_deal_breaker_skills: nonDealBreakerSkills,
    };

    jobDetails.primary_skills = finalSkillsData
      .flat()
      .map((item) => item.skill);
    jobDetails.other_skills = nonDealBreakerSkills?.map((item) => item.skill);
  }

  const pickedFields = pick(jobDetails, ["id", ...pickFields]);

  return pickedFields;
};
